import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import * as turf from '@turf/turf';
import { FlightPlanSettings, RouteAnalytics, RouteAnalyticsSegment } from './types';
import { assert } from '@deck.gl/core';
import { DEFAULT_ROUTE_WIDTH } from './routes-layer';
import { fix180Longitude } from '@fe/core';
import { Position } from 'geojson';

export const SOURCE = 'fe-route-ai-source';
export const LINE = 'fe-route-ai-line';
export const LABELS = 'fe-route-ai-labels';
export const POINTS = 'fe-route-ai-line-pts';

export class AIRoutesHelper {
    private map: MaplibreMap | null = null;
    private _settings: FlightPlanSettings | null = null;

    constructor(
        private readonly beforeId?: string
    ) {
    }

    get settings(): FlightPlanSettings | null {
        return this._settings;
    }

    set settings(value: FlightPlanSettings | null) {
        this._settings = value;
        this.showHide();
    }

    addToMap(map: MaplibreMap) {
        this.map = map;
        assert(this.map);

        this.map.addSource(SOURCE, {
            type: 'geojson',
            data: turf.featureCollection([])
        });

        this.map.addLayer(
            {
                id: LINE,
                type: 'line',
                source: SOURCE,
                paint: {
                    'line-width': DEFAULT_ROUTE_WIDTH,
                    'line-color': "#0f0"
                }
            },
            this.beforeId
        );
        this.map.addLayer(
            {
                id: POINTS,
                type: 'symbol',
                source: SOURCE,
                layout: {
                    'icon-image': 'fe:triangle-filled',
                    'icon-allow-overlap': true,
                    'icon-size': 0.3,
                    'symbol-placement': 'point'
                },
                paint: {
                    'icon-opacity': 0.2,
                    'icon-color': ['global-state', 'route-text-color']
                }
            },
            this.beforeId
        );
        this.map.addLayer(
            {
                id: LABELS,
                type: 'symbol',
                source: SOURCE,
                layout: {
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10,
                    'text-field': ['get', 'sid'],
                    'text-anchor': 'top-left',
                    'text-justify': 'left',
                    'icon-allow-overlap': true,
                    'text-allow-overlap': true,
                    'text-ignore-placement': true,
                    'text-optional': false,
                    'text-offset': [0.0, 0.7]
                },
                paint: {
                    'text-color': ['global-state', 'route-text-color']
                }
            },
            this.beforeId
        );

        this.showHide();
    }

    removeFromMap() {
        assert(this.map);

        this.map.removeLayer(LINE);
        this.map.removeLayer(POINTS);
        this.map.removeLayer(LABELS);
        this.map.removeSource(SOURCE);
        this.map = null;
    }

    remove() {
        assert(this.map);

        const source = this.map.getSource(SOURCE) as GeoJSONSource;
        source.updateData({
            removeAll: true
        });
    }

    removeAll() {
        const source = this.map?.getSource(SOURCE) as GeoJSONSource;
        source?.updateData({
            removeAll: true
        });
    }

    upsert(routes: RouteAnalytics | null) {
        const source = this.map!.getSource(SOURCE) as GeoJSONSource;
        assert(source);

        const features = routes?.segments ? this.toFeatures(routes?.segments) : undefined;
        source.updateData({
            add: features,
            removeAll: true,
        });
    }

    private showHide() {
        if (!this.map || !this.settings) {
            return;
        }

        const { show, showPoints, showLabels, showAi } = this.settings;
        const showAndAi = show && showAi;
        
        this.map.setLayoutProperty(LINE, 'visibility', showAndAi ? 'visible' : 'none');
        this.map.setLayoutProperty(POINTS, 'visibility', showAndAi && showPoints ? 'visible' : 'none');
        this.map.setLayoutProperty(LABELS, 'visibility', showAndAi && showPoints && showLabels ? 'visible' : 'none');
    }


    private toFeatures(segments: RouteAnalyticsSegment[]): GeoJSON.Feature[] {
        const features: GeoJSON.Feature[] = [];
        let ndx = 0;
        for(const segment of segments){
            features.push( turf.point(segment.startPosition, {sid: segment.sid}, {id: `ai-${ndx}-p`}));
            const lineFeature = toGreatCircleFeature(segment);
            if(lineFeature){
                lineFeature.id = `ai-${ndx}-l`;
                features.push(lineFeature);
            }
            ++ndx;
        }
        // Add last point
        if(segments.length > 0){
            const s = segments[segments.length - 1];
            features.push( turf.point(s.endPosition, {sid:s.sid}, {id: `ai-${ndx}-p`}));
        }
        
        return features;
    }
}

function toGreatCircleFeature(segment: RouteAnalyticsSegment | null ): GeoJSON.Feature | null {
    if (!segment) {
        return null;
    }

        const feature = turf.greatCircle(segment.startPosition, segment.endPosition, {
            npoints: 10 // TODO: make dynamic based on length
        });
        if (feature.geometry.type === 'LineString') {
            fixSegment180(feature.geometry.coordinates);
        }

        return feature;
    } 

function fixSegment180(segments: Position[] | null) {
    // Apply correction when segment lng changes from -180 to 180 or from 180 to -180
    if (segments && segments.length > 1 && Math.abs(segments[0][0] - segments[segments.length - 1][0]) > 180) {
        for (let i = 0; i < segments.length - 1; i++) {
            const fixedLng = fix180Longitude(segments[i][0], segments[i + 1][0]);
            segments[i + 1] = [fixedLng, segments[i + 1][1]];
        }
    }
}
